import { useState, useMemo, useCallback } from "react";
import { Button, Input } from "@aidkitorg/component-library";
import { usePost } from "../../../API";
import { useLocalizedStrings } from "../../../Localization";
import { useConnectionModal } from "./ConnectionModalContext";
import { SourceHeader } from "./SourceHeader";
import { type ConnectionModalState } from "./types";

const VERIZON_SOURCE_ID = "4fb7c245-daef-4e7d-af4a-69c86551bb71";
const VERIZON_AUTHORIZATION_TEXT_PREFIX = "We sent you an authorization request.";

interface ConnectionModalAdditionalQuestionPageProps {
  state: ConnectionModalState & { stage: "additional_question" };
}

export function ConnectionModalAdditionalQuestionPage({
  state,
}: ConnectionModalAdditionalQuestionPageProps) {
  const L = useLocalizedStrings();
  const answerAdditionalQuestion = usePost("/deck/answer_additional_question");
  const { token, onUpdateState, onExit } = useConnectionModal();

  const [answer, setAnswer] = useState("");

  // Some additional questions don't actually require a response.
  // For example, with Verizon, they ask you to respond to a text they sent you.
  // In these cases, we don't want to show the input to the user
  // as there's nothing for them to enter.
  const questionRequiresResponse = useMemo(() => {
    if (state.selectedSource.source_id === VERIZON_SOURCE_ID && state.question.startsWith(VERIZON_AUTHORIZATION_TEXT_PREFIX)) {
      return false;
    }

    return true;
  }, [state]);

  const handleChangeAnswer = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(e.currentTarget.value);
  }, []);

  const handleClickSubmit = useCallback(async () => {
    await answerAdditionalQuestion({
      link_token: token,
      answer,
    });

    onUpdateState({
      stage: "pending",
      selectedSource: state.selectedSource,
      connectionId: state.connectionId,
    });
  }, [answer, state]);

  return <div>
    <SourceHeader source={state.selectedSource} />
    {questionRequiresResponse &&
      <Input
        label={state.question}
        value={answer}
        onChange={handleChangeAnswer}
        dir="ltr" // This question from Deck will always be in English
      />
    }
    <div className="mt-6 flex justify-between">
      <Button onClick={onExit} variant="secondary">
        {L.component_library.modal.close}
      </Button>
      <Button onClick={handleClickSubmit} variant="primary">
        {questionRequiresResponse ? L.submission.submit : L.apply.continue}
      </Button>
    </div>
  </div>
}
